import { useEffect } from 'react';
import { useForecastMap, mapAlive } from '../ForecastMapContext';
import { windTileTemplate } from '../../../lib/wind/urls';
import { paramForLevel } from '../../../lib/wind/levels';
import { barbAtPixel } from '../../../lib/wind/windField';
import { getDecodedTile } from './tileCache';
import { buildTerrainSampler, terrainMaskZoom } from '../../../lib/wind/terrainSampler';

const SOURCE_ID = 'wind:barbs';
const LAYER_ID = 'wind:barbs:symbols';

// Skysight samples barbs on a regular ~46px screen grid (measured at z13 vs its
// captured sampleStep/anchorZoom). We do the same: place a barb every SPACING
// CSS pixels across the viewport, reading the (overzoomed) raster value there.
const SPACING = 46;

// composite-ss serves uv_10 to z9, the rest to z7.
const maxZoomFor = (param, model) => (model === 'ICON' ? 7 : param === 'uv_10' ? 9 : 7);

// Barb symbol layout — same sprite + rotation convention as LaunchSitesLayer's
// pg_points barbs (icon-image = knots sprite name, icon-rotate = direction).
const barbLayout = {
  'icon-image': ['get', 'wspd'],
  'icon-rotate': ['to-number', ['get', 'wdir']],
  // Sprite is @2x; keep barbs small + crisp (Skysight renders them small).
  'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.3, 10, 0.42, 14, 0.5],
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-rotation-alignment': 'map',
  'icon-anchor': 'bottom',
  'icon-offset': [3, 3],
};

const lngLatToTile = (lng, lat, z) => {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n);
  return [Math.max(0, Math.min(n - 1, x)), Math.max(0, Math.min(n - 1, y))];
};

const WindBarbsLayer = ({ base, date, time, product, level, model }) => {
  const map = useForecastMap();
  const param = product && (product.levelStepped ? paramForLevel(level) : product.param);

  useEffect(() => {
    if (!map || !param || !date || !time) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const range = product.range;
    const template = windTileTemplate({ base, date, time, param, model });
    const maskLevel = product.levelStepped ? level : null;

    const ensureLayer = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      }
      if (!map.getLayer(LAYER_ID)) {
        map.addLayer({ id: LAYER_ID, type: 'symbol', source: SOURCE_ID, layout: barbLayout });
      }
    };

    const refresh = async () => {
      if (cancelled) return;
      const z = Math.max(3, Math.min(maxZoomFor(param, model), Math.round(map.getZoom())));
      const b = map.getBounds();
      const [x0, y0] = lngLatToTile(b.getWest(), b.getNorth(), z);
      const [x1, y1] = lngLatToTile(b.getEast(), b.getSouth(), z);
      const coords = [];
      for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
        for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) coords.push([x, y]);
      }
      if (coords.length > 24) { ensureLayer(); return; } // guard zoomed-out fan-out
      try {
        const tiles = {};
        await Promise.all(coords.map(async ([x, y]) => {
          const url = template.replace('{z}', z).replace('{x}', x).replace('{y}', y);
          const t = await getDecodedTile(url, controller.signal).catch(() => null);
          if (t) tiles[`${x}/${y}`] = t;
        }));
        if (cancelled) return;

        let maskAbove = null;
        if (maskLevel != null) {
          const bb = map.getBounds();
          const sampler = await buildTerrainSampler({
            getTile: getDecodedTile, base, z: terrainMaskZoom(map.getZoom()),
            west: bb.getWest(), south: bb.getSouth(), east: bb.getEast(), north: bb.getNorth(),
            signal: controller.signal,
          });
          if (cancelled) return;
          maskAbove = (lng, lat) => sampler.aboveLevel(lng, lat, maskLevel);
        }

        // Screen-space grid: one barb per SPACING css-px, reading the raster there.
        const canvas = map.getCanvas();
        const cw = canvas.clientWidth || canvas.width;
        const ch = canvas.clientHeight || canvas.height;
        const n = Math.pow(2, z);
        const features = [];
        for (let sy = SPACING / 2; sy < ch; sy += SPACING) {
          for (let sx = SPACING / 2; sx < cw; sx += SPACING) {
            const ll = map.unproject([sx, sy]);
            const fx = ((ll.lng + 180) / 360) * n;
            const fy = ((1 - Math.asinh(Math.tan((ll.lat * Math.PI) / 180)) / Math.PI) / 2) * n;
            const tx = Math.floor(fx), ty = Math.floor(fy);
            const tile = tiles[`${tx}/${ty}`];
            if (!tile) continue;
            if (maskAbove && maskAbove(ll.lng, ll.lat)) continue; // above terrain → no barb
            const px = Math.min(tile.w - 1, Math.floor((fx - tx) * tile.w));
            const py = Math.min(tile.h - 1, Math.floor((fy - ty) * tile.h));
            const barb = barbAtPixel(tile.data, tile.w, tile.h, px, py, range);
            if (!barb) continue;
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [ll.lng, ll.lat] },
              properties: { wspd: barb.wspd, wdir: barb.wdir },
            });
          }
        }
        ensureLayer();
        const src = map.getSource(SOURCE_ID);
        if (src) src.setData({ type: 'FeatureCollection', features });
      } catch (e) { /* aborted or transient — ignore */ }
    };

    ensureLayer();
    refresh();
    // One-shot `idle` covers the initial load/settle (when no `moveend` fires —
    // e.g. the map opens directly at the pin's zoom); `moveend` covers later
    // pans/zooms. `once` avoids a refetch loop (setData itself triggers idle).
    map.once('idle', refresh);
    map.on('moveend', refresh);

    return () => {
      cancelled = true;
      controller.abort();
      map.off('idle', refresh);
      map.off('moveend', refresh);
      if (!mapAlive(map)) return; // map already destroyed (route unmount)
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [map, base, date, time, param, product, level, model]);

  return null;
};

export default WindBarbsLayer;
